import React from 'react';
import {
    Box,
    Dialog,
    Typography,
    type SxProps,
    Tabs,
    Tab,
    IconButton,
    List,
    ListItem,
    Divider,
    Stack,
    TextField,
} from '@mui/material';
import { mdiClose } from '@mdi/js';
import type {
    ISettingsDataObject,
    ISettingsDialogTabData,
} from '../../General/UserControl/UserSettingsDialogConfig';
import i18next from 'i18next';
import { genRandomKey } from '../../../utils';
import Icon from '@mdi/react';
import { useDarkMode } from '../../../DarkModeContext';

/** Styles for Dialog's component. */
const dialogSx: SxProps = {
    '& .MuiDialog-paper': {
        width: '80%',
        height: '60%',
        minWidth: '350px',
        overflow: 'hidden',
    },
};

/** Styles for Dialog's content. */
const dialogContentSx: SxProps = {
    flexGrow: 1,
    display: 'flex',
};

/**
 * TabPanel Component for each tab.
 */
const TabPanel = ({ data, title, value, index, onClose, isDark, ...other }: ITabPanelProps) => {
    const textColor = isDark ? '#e0e0e0' : '#434343';

    const textFieldSx: SxProps = {
        flex: 1,
        '.MuiInput-root.MuiInputBase-root': {
            '&:after': {
                borderBottom: 'none',
                color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0, 0, 0, 0.42)',
            },
            '&:hover': {
                '&:before': {
                    borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0, 0, 0, 0.42)'}`,
                },
            },
        },
        'label.Mui-focused': {
            color: isDark ? '#88D2E7' : 'rgba(0, 0, 0, 0.6)',
        },
        '& .MuiInputBase-input': {
            color: textColor,
            WebkitTextFillColor: textColor,
        },
        '& .MuiInputLabel-root': {
            color: isDark ? '#aaa' : undefined,
        },
        '& .MuiInput-underline:before': {
            borderBottomColor: isDark ? 'rgba(255,255,255,0.2)' : undefined,
        },
    };

    return (
        <Box
            role="tabpanel"
            hidden={value !== index}
            id={`vertical-tabpanel-${index}`}
            aria-labelledby={`vertical-tab-${index}`}
            sx={{
                flexGrow: 1,
                p: '0.5rem',
                backgroundColor: isDark ? '#1e1e1e' : undefined,
                color: textColor,
            }}
            {...other}
        >
            {value === index && (
                <>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                fontFamily: '"CeraPro Bold", sans-serif',
                                fontSize: 18,
                                color: textColor,
                                textTransform: 'capitalize',
                            }}
                        >
                            {i18next.t(title)}
                        </Typography>
                        <IconButton
                            aria-label="close"
                            onClick={onClose}
                            sx={{ color: isDark ? '#e0e0e0' : undefined }}
                        >
                            <Icon path={mdiClose} size={1} />
                        </IconButton>
                    </Box>
                    <Divider sx={{ borderColor: isDark ? '#3a3a3a' : undefined }} />
                    <List>
                        {data.map((d, i) => (
                            <ListItem
                                key={`${d.label}-${i}`}
                                sx={{
                                    alignItems: d?.viewItemComponentProps?.align ?? 'center',
                                    flexDirection: d?.viewItemComponentProps?.direction ?? 'row',
                                }}
                            >
                                <Stack
                                    direction={d.label === 'stack' ? 'column' : 'row'}
                                    sx={{ flex: 1, width: '100%' }}
                                >
                                    <TextField
                                        id={`field_${d.label}`}
                                        label={
                                            ['layout', 'stack'].includes(d.label)
                                                ? ''
                                                : i18next.t(d.label ?? `id-${genRandomKey()}`)
                                        }
                                        defaultValue={i18next.t(d.value)}
                                        variant="standard"
                                        fullWidth
                                        multiline={d?.viewItemComponentProps?.multiline}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        InputProps={{
                                            readOnly: true,
                                            startAdornment: d.startAdornment ?? null,
                                        }}
                                        inputProps={
                                            d?.viewItemComponentProps?.inputProps
                                                ? d.viewItemComponentProps.inputProps
                                                : {}
                                        }
                                        sx={textFieldSx}
                                    />
                                    {d.children && d.children()}
                                </Stack>
                            </ListItem>
                        ))}
                    </List>
                </>
            )}
        </Box>
    );
};

/**
 * Dialog Component that provides settings info.
 */
export default ({ open, handleClose, data }: SettingsDialogProps) => {
    const { isDark } = useDarkMode();
    const [value, setValue] = React.useState(0);

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
    };

    const tabsSx: SxProps = {
        borderRight: 1,
        borderColor: isDark ? '#3a3a3a' : '#DEDEDE',
        minWidth: '140px',
        padding: '0.5rem',
        backgroundColor: isDark ? '#181818' : undefined,
        '.MuiTabs-indicator': {
            display: 'none',
        },
    };

    const tabSx: SxProps = {
        fontFamily: '"CeraPro Regular", sans-serif',
        width: '100%',
        textTransform: 'capitalize',
        justifyContent: 'flex-start',
        flexDirection: 'row',
        minHeight: 'auto',
        fontSize: 16,
        color: isDark ? '#aaa' : '#434343',
        p: 1.5,
        '&.Mui-selected': {
            color: isDark ? '#e0e0e0' : '#434343',
            backgroundColor: isDark ? '#1a3a45' : '#EDF7EB',
        },
        '&:hover': {
            backgroundColor: isDark ? '#2a3a40' : '#f5f5f5',
            color: isDark ? '#e0e0e0' : '#434343',
        },
        '& .MuiTab-iconWrapper': {
            mb: 0,
            mr: 1,
            width: '24px',
            height: '24px',
        },
    };

    return (
        <Dialog
            onClose={handleClose}
            open={open}
            sx={{
                ...dialogSx,
                '& .MuiDialog-paper': {
                    ...(dialogSx['& .MuiDialog-paper'] as object),
                    backgroundColor: isDark ? '#1e1e1e' : undefined,
                    color: isDark ? '#e0e0e0' : undefined,
                },
            }}
        >
            <Box sx={dialogContentSx}>
                <Tabs
                    orientation="vertical"
                    variant="scrollable"
                    value={value}
                    onChange={handleChange}
                    sx={tabsSx}
                >
                    {Object.keys(data).map(tab => (
                        <Tab
                            key={tab}
                            icon={
                                <Icon
                                    path={data[tab].icon}
                                    size={1}
                                    color={isDark ? '#aaa' : undefined}
                                />
                            }
                            label={i18next.t(data[tab].name)}
                            id={`vertical-tab-${tab}`}
                            aria-controls={`vertical-tabpanel-${tab}`}
                            sx={tabSx}
                        />
                    ))}
                </Tabs>
                {Object.keys(data).map((tab, index) => (
                    <TabPanel
                        key={tab}
                        data={data[tab].data}
                        title={data[tab].name}
                        value={value}
                        index={index}
                        onClose={handleClose}
                        isDark={isDark}
                    />
                ))}
            </Box>
        </Dialog>
    );
};

/**
 * Interface that describes TabPanel's props.
 */
interface ITabPanelProps {
    title: string;
    data: ISettingsDialogTabData[];
    index: number;
    value: number;
    onClose: () => void;
    isDark: boolean;
}

/**
 * Interface that describes SettingsDialog's props.
 */
interface SettingsDialogProps {
    open: boolean;
    handleClose: () => void;
    data: ISettingsDataObject;
}
